import { useState, useEffect, useCallback, useMemo } from 'react';

const TYPE_OPTIONS = [
  { value: 'info',    label: 'Info',    color: '#3b82f6' },
  { value: 'warning', label: 'Warning', color: '#f59e0b' },
  { value: 'success', label: 'Success', color: '#10b981' },
  { value: 'danger',  label: 'Danger',  color: '#ef4444' },
];

const BLANK = { title: '', body: '', type: 'info', active: true, dismissable: true, email_on_create: false, expires_at: '', cta_label: '', cta_url: '' };
const BLANK_FILTER = { serviceFilter: 'all', planFilter: 'all', intervalFilter: 'all', statusFilter: 'all' };

const STATUS_OPTIONS = [
  { value: 'active',             label: 'Active' },
  { value: 'trialing',           label: 'Trialing' },
  { value: 'past_due',           label: 'Past due' },
  { value: 'unpaid',             label: 'Unpaid' },
  { value: 'incomplete',         label: 'Incomplete' },
  { value: 'incomplete_expired', label: 'Incomplete expired' },
  { value: 'canceled',           label: 'Canceled' },
  { value: 'paused',             label: 'Paused' },
];

function RecipientFilter({ value, onChange, services, plansByService, style }) {
  const plans = useMemo(() => {
    if (!value.serviceFilter || value.serviceFilter === 'all') return [];
    return plansByService[value.serviceFilter] || [];
  }, [value.serviceFilter, plansByService]);

  const set = (key, val) => {
    const next = { ...value, [key]: val };
    if (key === 'serviceFilter') { next.planFilter = 'all'; next.intervalFilter = 'all'; next.statusFilter = 'all'; }
    if (key === 'planFilter') { next.intervalFilter = 'all'; next.statusFilter = 'all'; }
    if (key === 'intervalFilter') { next.statusFilter = 'all'; }
    onChange(next);
  };

  const sel = (key, opts, placeholder) => (
    <select className="admin-select" value={value[key]} onChange={e => set(key, e.target.value)}
      style={{ fontSize: '12px', padding: '4px 8px', height: '30px', minWidth: '140px' }}>
      <option value="all">{placeholder}</option>
      {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', ...style }}>
      {sel('serviceFilter', services.filter(s => s.status !== 'coming_soon').map(s => ({ value: s.key, label: s.name + ' subscribers' })), 'All verified users')}
      {value.serviceFilter !== 'all' && sel('planFilter', plans.map(p => ({ value: p.slug, label: p.name })), 'All plans')}
      {value.serviceFilter !== 'all' && sel('intervalFilter', [{ value: 'month', label: 'Monthly' }, { value: 'year', label: 'Yearly' }], 'All billing cycles')}
      {value.serviceFilter !== 'all' && sel('statusFilter', STATUS_OPTIONS, 'All statuses')}
    </div>
  );
}

export default function AdminAnnouncements({ apiFetch, toast }) {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(BLANK);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [blasting, setBlasting] = useState({});

  // Service/plan data for filter
  const [services, setServices] = useState([]);
  const [plansByService, setPlansByService] = useState({});

  // Filter state: { serviceFilter, planFilter, intervalFilter }
  const [createFilter, setCreateFilter] = useState(BLANK_FILTER);
  const [blastFilter, setBlastFilter] = useState({});  // { [annId]: filter object }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch('/api/admin/announcements');
      const d = await r.json();
      setAnnouncements(d.announcements || []);
    } catch { toast('Failed to load announcements', 'error'); }
    setLoading(false);
  }, [apiFetch, toast]);

  useEffect(() => {
    load();
    Promise.all([
      apiFetch('/api/admin/services').then(r => r.json()),
      apiFetch('/api/admin/plans').then(r => r.json()),
    ]).then(([svcs, plansData]) => {
      const list = Array.isArray(svcs) ? svcs : [];
      setServices(list);
      const allPlans = Array.isArray(plansData) ? plansData : [];
      const byService = {};
      for (const p of allPlans) {
        if (!byService[p.service_key]) byService[p.service_key] = [];
        byService[p.service_key].push(p);
      }
      setPlansByService(byService);
    }).catch(() => {});
  }, [load]);

  const startEdit = (ann) => {
    setEditId(ann.id);
    setForm({ title: ann.title, body: ann.body, type: ann.type, active: !!ann.active, dismissable: !!ann.dismissable, email_on_create: false, expires_at: ann.expires_at ? ann.expires_at.slice(0, 16) : '', cta_label: ann.cta_label || '', cta_url: ann.cta_url || '' });
    setCreateFilter(BLANK_FILTER);
  };

  const cancelEdit = () => { setEditId(null); setForm(BLANK); setCreateFilter(BLANK_FILTER); };

  const handleSave = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast('Title and body are required', 'error'); return; }
    setSaving(true);
    try {
      const payload = { ...form, expires_at: form.expires_at || null, cta_label: form.cta_label.trim() || null, cta_url: form.cta_url.trim() || null };
      if (!editId && form.email_on_create) {
        payload.serviceFilter  = createFilter.serviceFilter  !== 'all' ? createFilter.serviceFilter  : undefined;
        payload.planFilter     = createFilter.planFilter     !== 'all' ? createFilter.planFilter     : undefined;
        payload.intervalFilter = createFilter.intervalFilter !== 'all' ? createFilter.intervalFilter : undefined;
        payload.statusFilter   = createFilter.statusFilter   !== 'all' ? createFilter.statusFilter   : undefined;
      }
      if (editId) {
        const r = await apiFetch(`/api/admin/announcements/${editId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        if (!r.ok) throw new Error();
        toast('Announcement updated');
      } else {
        const r = await apiFetch('/api/admin/announcements', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        if (!r.ok) throw new Error();
        toast(form.email_on_create ? 'Announcement created & email queued' : 'Announcement created');
      }
      setEditId(null); setForm(BLANK); setCreateFilter(BLANK_FILTER); load();
    } catch { toast('Failed to save announcement', 'error'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await apiFetch(`/api/admin/announcements/${id}`, { method: 'DELETE' });
      toast('Deleted'); load();
    } catch { toast('Failed to delete', 'error'); }
  };

  const handleToggleActive = async (ann) => {
    try {
      await apiFetch(`/api/admin/announcements/${ann.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ active: !ann.active }) });
      load();
    } catch { toast('Failed to update', 'error'); }
  };

  const handleEmailBlast = async (ann) => {
    const f = blastFilter[ann.id] || BLANK_FILTER;
    const parts = [];
    if (f.serviceFilter !== 'all') parts.push(services.find(s => s.key === f.serviceFilter)?.name || f.serviceFilter);
    if (f.planFilter !== 'all') parts.push(f.planFilter);
    if (f.intervalFilter !== 'all') parts.push(f.intervalFilter === 'year' ? 'yearly' : 'monthly');
    if (f.statusFilter !== 'all') parts.push(STATUS_OPTIONS.find(o => o.value === f.statusFilter)?.label || f.statusFilter);
    const filterLabel = parts.length ? parts.join(' · ') + ' subscribers' : 'all verified users';
    if (!window.confirm(`Send this announcement to ${filterLabel}${ann.email_sent ? ' (already sent once)' : ''}?`)) return;
    setBlasting(b => ({ ...b, [ann.id]: true }));
    try {
      const body = {
        serviceFilter:  f.serviceFilter  !== 'all' ? f.serviceFilter  : undefined,
        planFilter:     f.planFilter     !== 'all' ? f.planFilter     : undefined,
        intervalFilter: f.intervalFilter !== 'all' ? f.intervalFilter : undefined,
        statusFilter:   f.statusFilter   !== 'all' ? f.statusFilter   : undefined,
      };
      const r = await apiFetch(`/api/admin/announcements/${ann.id}/email`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed');
      toast(`Email queued for ${d.queued} user${d.queued !== 1 ? 's' : ''}`);
      load();
    } catch { toast('Email blast failed', 'error'); }
    setBlasting(b => ({ ...b, [ann.id]: false }));
  };

  // Send an announcement to specific customers, entered as comma/space/newline-separated emails.
  const handleEmailSpecific = async (ann) => {
    const raw = window.prompt('Send this announcement to specific customers.\nEnter their email addresses (comma or newline separated):', '');
    if (raw === null) return;
    const recipients = [...new Set(raw.split(/[\s,;]+/).map(e => e.trim().toLowerCase()).filter(Boolean))];
    if (!recipients.length) { toast('No valid emails entered', 'error'); return; }
    if (!window.confirm(`Send "${ann.title}" to ${recipients.length} recipient${recipients.length !== 1 ? 's' : ''}?`)) return;
    setBlasting(b => ({ ...b, [ann.id]: true }));
    try {
      const r = await apiFetch(`/api/admin/announcements/${ann.id}/email`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ recipients }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed');
      toast(`Email queued for ${d.queued} recipient${d.queued !== 1 ? 's' : ''}`);
      load();
    } catch { toast('Targeted send failed', 'error'); }
    setBlasting(b => ({ ...b, [ann.id]: false }));
  };

  const getBlastFilter = (annId) => blastFilter[annId] || BLANK_FILTER;
  const setAnnFilter = (annId, f) => setBlastFilter(b => ({ ...b, [annId]: f }));

  const typeColor = (t) => TYPE_OPTIONS.find(o => o.value === t)?.color || '#3b82f6';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, fontSize: '18px', color: 'var(--admin-text-primary)' }}>Announcements</h2>
        {editId && <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={cancelEdit}>Cancel Edit</button>}
      </div>

      {/* Create / Edit form */}
      <div className="admin-card" style={{ marginBottom: '24px', padding: '20px' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '700', color: 'var(--admin-text-primary)' }}>
          {editId ? 'Edit Announcement' : 'New Announcement'}
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
          <div>
            <label className="admin-label">TITLE</label>
            <input className="admin-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Announcement title" />
          </div>
          <div>
            <label className="admin-label">TYPE</label>
            <select className="admin-select" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
              {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <div style={{ marginBottom: '12px' }}>
          <label className="admin-label">BODY</label>
          <textarea className="admin-input" rows={3} value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} placeholder="Announcement message shown to users…" style={{ resize: 'vertical' }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '12px' }}>
          <div>
            <label className="admin-label">CTA BUTTON LABEL <span style={{ color: 'var(--admin-text-muted)', fontWeight: '400' }}>(optional)</span></label>
            <input className="admin-input" value={form.cta_label} onChange={e => setForm(f => ({ ...f, cta_label: e.target.value }))} placeholder="e.g. Learn more" />
          </div>
          <div>
            <label className="admin-label">CTA URL</label>
            <input className="admin-input" type="url" value={form.cta_url} onChange={e => setForm(f => ({ ...f, cta_url: e.target.value }))} placeholder="https://..." />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
          <div>
            <label className="admin-label">EXPIRES AT (optional)</label>
            <input className="admin-input" type="datetime-local" value={form.expires_at} onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', justifyContent: 'flex-end' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--admin-text-secondary)' }}>
              <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} /> Active (visible to users)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--admin-text-secondary)' }}>
              <input type="checkbox" checked={form.dismissable} onChange={e => setForm(f => ({ ...f, dismissable: e.target.checked }))} /> Dismissable by user
            </label>
            {!editId && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#f59e0b' }}>
                  <input type="checkbox" checked={form.email_on_create} onChange={e => setForm(f => ({ ...f, email_on_create: e.target.checked }))} /> Email users on create
                </label>
                {form.email_on_create && (
                  <div style={{ paddingLeft: '20px' }}>
                    <label className="admin-label" style={{ fontSize: '10px', marginBottom: '4px' }}>RECIPIENTS</label>
                    <RecipientFilter value={createFilter} onChange={setCreateFilter} services={services} plansByService={plansByService} />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Preview */}
        {(form.title || form.body) && (
          <div style={{ marginBottom: '16px', borderLeft: `4px solid ${typeColor(form.type)}`, padding: '10px 14px', background: 'var(--admin-bg)', borderRadius: '0 6px 6px 0', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontWeight: '700', marginRight: '6px', color: typeColor(form.type) }}>{form.title}</span>
              <span style={{ color: 'var(--admin-text-secondary)' }}>{form.body}</span>
            </div>
            {form.cta_label && (
              <span style={{ display: 'inline-block', padding: '3px 12px', borderRadius: '20px', border: `1px solid ${typeColor(form.type)}`, color: typeColor(form.type), fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap' }}>
                {form.cta_label} →
              </span>
            )}
          </div>
        )}

        <button className="admin-btn admin-btn--primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : editId ? 'Save Changes' : 'Create Announcement'}
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ color: 'var(--admin-text-muted)', fontSize: '13px' }}>Loading…</div>
      ) : announcements.length === 0 ? (
        <div className="admin-card" style={{ padding: '32px', textAlign: 'center', color: 'var(--admin-text-muted)', fontSize: '14px' }}>
          No announcements yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {announcements.map(ann => (
            <div key={ann.id} className="admin-card" style={{ padding: '14px 18px', borderLeft: `4px solid ${typeColor(ann.type)}`, opacity: ann.active ? 1 : 0.55 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: '700', fontSize: '14px', color: 'var(--admin-text-primary)' }}>{ann.title}</span>
                    <span style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', padding: '1px 7px', borderRadius: '10px', background: typeColor(ann.type) + '22', color: typeColor(ann.type) }}>{ann.type}</span>
                    {!ann.active && <span style={{ fontSize: '10px', color: 'var(--admin-text-muted)', border: '1px solid var(--admin-border)', borderRadius: '10px', padding: '1px 7px' }}>inactive</span>}
                    {ann.email_sent ? <span style={{ fontSize: '10px', color: '#10b981' }}><i className="fa-solid fa-envelope-circle-check" style={{ marginRight: 3 }} />emailed</span> : null}
                    {ann.expires_at && <span style={{ fontSize: '10px', color: 'var(--admin-text-muted)' }}>expires {new Date(ann.expires_at).toLocaleDateString()}</span>}
                  </div>
                  <p style={{ margin: '0 0 6px 0', fontSize: '13px', color: 'var(--admin-text-secondary)', whiteSpace: 'pre-wrap' }}>{ann.body}</p>
                  <div style={{ fontSize: '11px', color: 'var(--admin-text-muted)' }}>
                    Created {new Date(ann.created_at).toLocaleString()} {ann.created_by ? `by ${ann.created_by}` : ''}
                    {ann.dismissable ? ' · dismissable' : ' · persistent'}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0, alignItems: 'flex-end' }}>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <button className="admin-btn admin-btn--sm admin-btn--secondary" onClick={() => handleToggleActive(ann)}>
                      {ann.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button className="admin-btn admin-btn--sm admin-btn--secondary" onClick={() => startEdit(ann)}>
                      <i className="fa-solid fa-pen" />
                    </button>
                    <button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => handleDelete(ann.id)}>
                      <i className="fa-solid fa-trash" />
                    </button>
                  </div>
                  {/* Email blast with deep filter */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end' }}>
                    <RecipientFilter
                      value={getBlastFilter(ann.id)}
                      onChange={f => setAnnFilter(ann.id, f)}
                      services={services}
                      plansByService={plansByService}
                    />
                    <div style={{ display: 'flex', gap: '6px', alignSelf: 'flex-end' }}>
                      <button
                        className="admin-btn admin-btn--sm admin-btn--secondary"
                        onClick={() => handleEmailSpecific(ann)}
                        disabled={blasting[ann.id]}
                        title="Send this announcement to specific customers by email"
                      >
                        <i className="fa-solid fa-user" />{' '}Specific…
                      </button>
                      <button
                        className="admin-btn admin-btn--sm admin-btn--primary"
                        onClick={() => handleEmailBlast(ann)}
                        disabled={blasting[ann.id]}
                        title="Queue email blast for selected recipients"
                      >
                        {blasting[ann.id] ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-envelope" />}
                        {' '}Email
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
